import { useAppDispatch } from "../../state/hooks";
import { closeConfirmationModal } from "../../state/slice/modal/modalSlice";
import ModalStandard from "./ModalStandard";
import { IsLoading } from "../AppState/IsLoading";
import {
  MediaModules,
  type IEntitiesToDisplayWarning,
} from "src/features/gallery/components/MediaModuleWarning";
import { buildMediaUsageEntities } from "src/features/gallery/util/buildMediaUsageEntities";
import Spinner from "../Loader/Spinner";
import {
  MEDIA_CHECK_BEFORE_DELETE_ID,
  MEDIA_COMPONENT_ID,
  MEDIA_MULTIPLE_MEDIA_DELETE_COMPONENT_ID,
} from "src/features/gallery/components/MediaList";
import { useEffect, useRef, useState } from "react";
import {
  deleteMedia,
  deleteMultipleMediaFromIds,
  checkIfMediasAreUsed,
} from "src/features/gallery/state/gallerySlice";
import { handleMediaPlaceholderRefreshAfterDeletion } from "../../../common/util/handleMediaPlaceholderRefreshAfterDeletion";
import type { IMedia } from "src/model/unityObject";
import { useTranslation } from "react-i18next";
import { setAlert } from "src/features/alert/state/alertsSlice";

type ModalStandardProps = {
  id: string;
  text?: string;
  text2?: string;
  componentId?: string;
  isMediaList?: boolean;
  idMedia?: number;
  mediasIdToDeleteThatIsChecked?: Array<string | number>;
  mediasToDelete?: IMedia[];
};

const ModalConfirmationToDeleteMedia = ({
  id,
  text = "",
  text2 = "",
  componentId,
  isMediaList = false,
  idMedia = -1,
  mediasIdToDeleteThatIsChecked = [],
  mediasToDelete = [],
}: ModalStandardProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [entitiesToDisplayWarning, setEntitiesToDisplayWarning] =
    useState<IEntitiesToDisplayWarning>({
      projects: [],
      users: [],
      organizations: [],
    });
  const [isComputingFinished, setIsComputingFinished] = useState(true);

  const prevIdMediaRef = useRef<number>();
  const prevMediasIdToDeleteThatIsCheckedRef = useRef<Array<string | number>>();
  const prevMediasToDeleteRef = useRef<IMedia[]>();

  const zeroEntitiesToDisplayWarning =
    entitiesToDisplayWarning.users.length === 0 &&
    entitiesToDisplayWarning.projects.length === 0 &&
    entitiesToDisplayWarning.organizations.length === 0;

  const moreThanZeroEntitiesToDisplayWarning =
    entitiesToDisplayWarning.users.length > 0 ||
    entitiesToDisplayWarning.projects.length > 0 ||
    entitiesToDisplayWarning.organizations.length > 0;

  const oneEntityToDisplayWarning =
    entitiesToDisplayWarning.users.length === 1 ||
    entitiesToDisplayWarning.projects.length === 1 ||
    entitiesToDisplayWarning.organizations.length === 1;

  const moreThanOneEntityToDisplayWarning =
    entitiesToDisplayWarning.users.length > 1 ||
    entitiesToDisplayWarning.projects.length > 1 ||
    entitiesToDisplayWarning.organizations.length > 1;

  const resetStates = () => {
    setEntitiesToDisplayWarning({ projects: [], users: [], organizations: [] });
    setIsComputingFinished(true);
  };

  const deleteMultipleMedias = () => {
    if (zeroEntitiesToDisplayWarning && mediasIdToDeleteThatIsChecked.length > 0) {
      void dispatch(
        deleteMultipleMediaFromIds({
          componentId: MEDIA_CHECK_BEFORE_DELETE_ID,
          mediaIds: mediasIdToDeleteThatIsChecked,
        }),
      ).then((res) => {
        if (deleteMultipleMediaFromIds.fulfilled.match(res)) {
          res.payload.forEach((elt) => {
            void handleMediaPlaceholderRefreshAfterDeletion({ payload: elt, dispatch });
          });
          dispatch(setAlert({ type: "success", msg: t("alerts.mediasDeleteSuccess") }));
        } else {
          dispatch(setAlert({ type: "danger", msg: t("alerts.mediasDeleteFail") }));
        }
      });
    }
    dispatch(closeConfirmationModal(id));
  };

  const deleteSingleMedia = () => {
    if (zeroEntitiesToDisplayWarning && idMedia !== -1) {
      void dispatch(deleteMedia({ componentId: MEDIA_COMPONENT_ID, mediaId: idMedia })).then(
        (res) => {
          if (deleteMedia.fulfilled.match(res)) {
            void handleMediaPlaceholderRefreshAfterDeletion({
              payload: res.payload,
              dispatch,
            });
            dispatch(setAlert({ type: "success", msg: t("alerts.mediaDeleteSuccess") }));
          } else {
            dispatch(setAlert({ type: "danger", msg: t("alerts.mediaDeleteFail") }));
          }
        },
      );
    }
    dispatch(closeConfirmationModal(id));
  };

  const toDisplayOrNotToDisplayWarning = async (medias: IMedia[]) => {
    if (!medias || medias.length === 0) {
      setIsComputingFinished(true);
      return;
    }
    setIsComputingFinished(false);

    const res = await dispatch(
      checkIfMediasAreUsed({
        mediaIDs: [medias],
        componentId: MEDIA_MULTIPLE_MEDIA_DELETE_COMPONENT_ID,
        isToDeleteOne: false,
      }),
    );

    if (checkIfMediasAreUsed.fulfilled.match(res)) {
      const usageEntities = buildMediaUsageEntities(res.payload.data);
      if (
        usageEntities.projects.length > 0 ||
        usageEntities.users.length > 0 ||
        usageEntities.organizations.length > 0
      ) {
        setEntitiesToDisplayWarning(usageEntities);
      }
    }
    setIsComputingFinished(true);
  };

  useEffect(() => {
    const prevMediasIdToDeleteThatIsCheckedJSON = JSON.stringify(
      prevMediasIdToDeleteThatIsCheckedRef.current,
    );
    const mediasIdToDeleteThatIsCheckedJSON = JSON.stringify(mediasIdToDeleteThatIsChecked);
    const prevMediasToDeleteJSON = JSON.stringify(prevMediasToDeleteRef.current);
    const mediasToDeleteJSON = JSON.stringify(mediasToDelete);

    if (
      prevIdMediaRef.current !== idMedia ||
      prevMediasIdToDeleteThatIsCheckedJSON !== mediasIdToDeleteThatIsCheckedJSON ||
      prevMediasToDeleteJSON !== mediasToDeleteJSON
    ) {
      void toDisplayOrNotToDisplayWarning(mediasToDelete);
    }

    prevIdMediaRef.current = idMedia;
    prevMediasIdToDeleteThatIsCheckedRef.current = mediasIdToDeleteThatIsChecked;
    prevMediasToDeleteRef.current = mediasToDelete;
  }, [mediasToDelete, mediasIdToDeleteThatIsChecked, idMedia]);

  useEffect(() => {
    return () => {
      resetStates();
    };
  }, [id]);

  const modalMessage = () => {
    if (isComputingFinished && mediasToDelete.length === 1 && zeroEntitiesToDisplayWarning) {
      return t("pages.gallery.deleteMedia.confirmMessage");
    } else if (isComputingFinished && mediasToDelete.length > 1 && zeroEntitiesToDisplayWarning) {
      return t("pages.gallery.deleteMedia.confirmMessagePlural");
    } else if (
      isComputingFinished &&
      mediasToDelete.length <= 1 &&
      (oneEntityToDisplayWarning || moreThanOneEntityToDisplayWarning)
    ) {
      return t("pages.gallery.deleteMedia.warningMessage");
    } else if (
      isComputingFinished &&
      mediasToDelete.length > 1 &&
      moreThanOneEntityToDisplayWarning
    ) {
      return t("pages.gallery.deleteMedia.warningMessagePlural");
    } else {
      return t("general.loading");
    }
  };

  return (
    <ModalStandard
      id={id}
      fullScreen={false}
      modalType={"confirmation"}
      className="w-2/3 h-fit max-h-[90vh] flex flex-col"
    >
      <div className="modalContent personalize-scroll-visible min-h-0 flex-1">
        {componentId && (
          <div className="z-20">
            <IsLoading componentId={componentId} showSuccess={false} />
          </div>
        )}
        <div>
          <p className={`${isMediaList ? "font-bold text-2xl mx-5" : "font-bold text-2xl"} my-4`}>
            {modalMessage()}
          </p>
          <p className="text-gray-600 mb-4">{text}</p>
          {text2 && <p className="text-gray-600 mb-4">{text2}</p>}
          <div>
            <IsLoading componentId={MEDIA_CHECK_BEFORE_DELETE_ID} showSuccess={false}>
              {isComputingFinished ? (
                <MediaModules entitiesToDisplayWarning={entitiesToDisplayWarning} />
              ) : (
                <Spinner />
              )}
            </IsLoading>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-3 shrink-0">
        {!moreThanZeroEntitiesToDisplayWarning && (
          <button
            type="button"
            className="btn-alternative-outline mr-4"
            onClick={() => {
              dispatch(closeConfirmationModal(id));
            }}
          >
            {String(t("general.cancel"))}
          </button>
        )}
        <button
          className="btn-primary-fill"
          type="button"
          disabled={!isComputingFinished}
          onClick={() => {
            if (mediasIdToDeleteThatIsChecked.length === 0) {
              deleteSingleMedia();
            } else {
              deleteMultipleMedias();
            }
          }}
        >
          {!isComputingFinished
            ? t("general.loading")
            : moreThanZeroEntitiesToDisplayWarning
            ? t("general.ok")
            : t("general.yes")}
        </button>
      </div>
    </ModalStandard>
  );
};

export default ModalConfirmationToDeleteMedia;
